import type { ConvoAIResponseRovoAction } from '../../../types/types';
import type { RequestOptions, ResponseObject } from './types';

/**
 * Name of the Convo AI final response field indicating the backend already persisted the edit.
 */
const APPLIED_SERVER_SIDE_KEY = 'applied_server_side';

/**
 * Reports whether a Convo AI final response says the backend has already persisted the edit to
 * the document.
 *
 * When true the generated content must not be applied locally - the change reaches the editor
 * through collaborative editing instead, and applying it again would duplicate the edit.
 *
 * Deliberately strict: only an explicit `true` counts. An absent or false field means the backend
 * did not persist anything, so the client retains ownership of applying the result.
 */
export function isAppliedServerSide(data: unknown): boolean {
	return (
		typeof data === 'object' &&
		data !== null &&
		(data as Record<string, unknown>)[APPLIED_SERVER_SIDE_KEY] === true
	);
}

/**
 * Builds the completion emitted when the backend owns the apply.
 *
 * Carries an explicit `appliedServerSide` flag rather than simply omitting the content, so that
 * consumers can distinguish a backend-owned apply from a generation that legitimately produced
 * nothing. Those two cases are otherwise identical on the wire and must not be conflated.
 */
export function createBackendAppliedResponse({
	cacheKey,
	requestOptions,
	rovoActions,
}: {
	cacheKey: string;
	requestOptions: Extract<RequestOptions, { payload: string }>;
	rovoActions: ConvoAIResponseRovoAction[];
}): ResponseObject {
	return {
		type: 'complete',
		appliedServerSide: true,
		markdown: '',
		cacheKey,
		modelInput: { selection: requestOptions.selection, doc: requestOptions.doc },
		rovoActions,
	};
}
